import useSWR from 'swr';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export const SIGNATURES_MANAGE_KEY = '/emails/signatures/manage';
export const SIGNATURES_LIST_KEY = '/emails/signatures/list';

/** True when user can manage all signatures (admin / email_signatures view). */
export const useCanManageEmailSignatures = (): boolean => {
  const { can } = useAuth();
  return can('email_signatures', 'view');
};

export interface AssignedSignatureUser {
  userId: number;
  name: string;
  isDefault: boolean;
}

export interface AssignedSignatureAccount {
  emailAccountId: number;
  emailAddress: string;
  displayName?: string | null;
  label: string;
}

export interface EmailSignature {
  id: number;
  name: string;
  body: string | null;
  createdByUserId: number;
  ownerName?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  assignedUsers: AssignedSignatureUser[];
  assignedUserIds: number[];
  assignedAccounts: AssignedSignatureAccount[];
  assignedAccountIds: number[];
  canSelfEdit?: boolean;
  /** True when the requester may edit (owner, or sole assignee). */
  canEdit?: boolean;
  /** True when assigned but not allowed to edit (multi-user non-owner). */
  isViewOnly?: boolean;
}

export interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail: string;
}

export interface AccountOption {
  id: number;
  emailAddress: string;
  displayName?: string | null;
  label: string;
}

export const formatUserLabel = (u: UserOption): string =>
  u.userDisplayName?.trim() ||
  [u.userFirstName, u.userLastName].filter(Boolean).join(' ') ||
  u.userEmail ||
  `User #${u.id}`;

export const formatAccountLabel = (a: Pick<AccountOption, 'displayName' | 'emailAddress' | 'id'>): string =>
  a.displayName?.trim() || a.emailAddress || `Account #${a.id}`;

/** List signatures: all for managers, assigned-only for regular users. */
export const useSignaturesList = () => {
  const { can, permissionsLoaded } = useAuth();
  const canManage = can('email_signatures', 'view');
  const url = permissionsLoaded
    ? canManage
      ? SIGNATURES_MANAGE_KEY
      : SIGNATURES_LIST_KEY
    : null;

  const { data, error, isLoading, mutate } = useSWR<EmailSignature[]>(
    url,
    (fetchUrl: string) => api.get(fetchUrl).then((r) => r.data),
  );
  return {
    data: data ?? [],
    isLoading: isLoading || !permissionsLoaded,
    isError: error,
    mutate,
    canManage,
  };
};

/** Get one signature by id (for edit form) */
export const useSignature = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<EmailSignature | null>(
    id ? `/emails/signatures/${id}` : null,
    (url: string) => api.get(url).then((r) => r.data)
  );
  return {
    data: data ?? null,
    isLoading,
    isError: error,
    mutate,
  };
};

/** All users (for assignment; managers only) */
export const useUsersForSignatureAssign = (enabled = true) => {
  const { data, error, isLoading, mutate } = useSWR<UserOption[]>(
    enabled ? '/users' : null,
    (url: string) => api.get(url).then((r) => (Array.isArray(r.data) ? r.data : []))
  );
  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

/** All email accounts (for assignment; managers only) */
export const useAccountsForSignatureAssign = (enabled = true) => {
  const { data, error, isLoading, mutate } = useSWR<AccountOption[]>(
    enabled ? '/emails/signatures/accounts' : null,
    (url: string) => api.get(url).then((r) => (Array.isArray(r.data) ? r.data : []))
  );
  return {
    data: data ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export type CreateSignaturePayload = {
  name: string;
  body?: string;
  isDefault?: boolean;
  userIds: number[];
  accountIds: number[];
};

export type UpdateSignaturePayload = {
  name?: string;
  body?: string;
  isDefault?: boolean;
  userIds?: number[];
  accountIds?: number[];
};

export const createSignature = async (payload: CreateSignaturePayload) => {
  const { data } = await api.post<EmailSignature>('/emails/signatures', payload);
  return data;
};

export const updateSignature = async (id: number, payload: UpdateSignaturePayload) => {
  const { data } = await api.patch<EmailSignature>(`/emails/signatures/${id}`, payload);
  return data;
};

export const cloneSignature = async (id: number): Promise<EmailSignature> => {
  const { data } = await api.post<EmailSignature>(`/emails/signatures/${id}/clone`);
  return data;
};

export const deleteSignature = async (id: number) => {
  await api.delete(`/emails/signatures/${id}`);
};
